type DatabaseStreamSnapshot = {
  isStreaming: boolean;
  operationType?: 'create' | 'edit';
};

export type DatabaseStreamListener = (state: DatabaseStreamSnapshot) => void;

import {
  DatabaseAgent,
  DatabasesAIFCProcessor,
  EntryProcessor,
  FieldProcessor,
  ROW_RENDER_DELAY_MS,
  ValueProcessor,
  ViewProcessor,
} from '@databases/ai-first-creation-token-processing';
import { MigrationManager } from '@databases/collab-migrations/migrations-manager';
import { collabStateManager } from '@databases/collab-state-manager';
import { StreamingStateManager } from '@databases/database-ai-first-creation-state';
import { viewState } from '@databases/database-state/state/view';
import { logError } from '@databases/logs';
import { type MutableDatabase } from '@databases/model/database';
import type { Token, CsvError, CsvErrorCollector } from '@databases/streaming-csv-parser';
import { StreamingCsvParser, TokenType } from '@databases/streaming-csv-parser';
import { DatabaseLayoutType } from '@databases/types/database/view';

import { buildScopedEditSnapshot } from './behaviour/database-aifc-analytics-snapshot';
import { DatabaseAifcAnalyticsSystem } from './behaviour/database-aifc-analytics-system';
import { fireAIResultActionedEvent } from './metrics/ai-result-actioned-event';

type DatabaseStreamOptions = {
  shouldClearContent?: boolean;
  operationType?: 'create' | 'edit';
  rowRenderDelayMs?: number;
};

type EditScopeTracker = {
  affectedRowNumbers: Set<string>;
  editedFieldsByRow: Map<string, Set<string>>;
  currentEditRowNumber: string | null;
  affectedViewNames: Set<string>;
};

export class DatabaseStreamState {
  private static instance: DatabaseStreamState | undefined;

  private isStreaming = false;
  private operationType: 'create' | 'edit' = 'create';
  private currentGroupId: string | undefined;
  private listeners = new Set<DatabaseStreamListener>();
  private tokenQueue: Token[] = [];
  private processingPromise: Promise<void> | null = null;
  private csvParser: StreamingCsvParser | null = null;
  private aifcProcessor: DatabasesAIFCProcessor | null = null;
  /*
   * Maps CSV row numbers to entry IDs. The serializer assigns sequential numeric
   * row IDs (1, 2, 3...) for readability, but entries have UUID-based IDs in storage.
   * Set by readDatabaseContent, used by EntryProcessor during edit operations.
   */
  private rowIdToEntryIdMappings: Map<string, string> = new Map();
  private rowRenderDelayMs = ROW_RENDER_DELAY_MS;
  private executedInitialWait = false;
  private lastErrors: CsvError[] = [];
  private analyticsSystem = new DatabaseAifcAnalyticsSystem(fireAIResultActionedEvent);
  private editScopeTracker: EditScopeTracker | null = null;

  static getInstance(): DatabaseStreamState {
    if (!DatabaseStreamState.instance) {
      DatabaseStreamState.instance = new DatabaseStreamState();
    }
    return DatabaseStreamState.instance;
  }

  public get isStreamingActive(): boolean {
    return this.isStreaming;
  }

  public get streamingGroupId(): string | undefined {
    return this.currentGroupId;
  }

  /**
   * Subscribe to streaming state updates.
   * Returns cleanup function
   */
  public subscribe(listener: DatabaseStreamListener): () => void {
    this.listeners.add(listener);
    listener({ isStreaming: this.isStreaming, operationType: this.operationType });
    return () => this.listeners.delete(listener);
  }

  private notify(): void {
    const snapshot: DatabaseStreamSnapshot = {
      isStreaming: this.isStreaming,
      operationType: this.operationType,
    };
    this.listeners.forEach((listener) => {
      try {
        listener(snapshot);
        // eslint-disable-next-line canvas/no-swallow-errors-in-catch
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        logError('Error in DatabaseStreamState listener', error);
      }
    });
  }

  public processChunk(data: string): void {
    if (!this.isStreaming || !this.csvParser) {
      throw new Error('Streaming not active. Parser not initialized.');
    }

    this.csvParser.onReceiveChunk(data);

    if (!this.processingPromise) {
      this.processingPromise = this.processQueueAsync();
      this.processingPromise.finally(() => {
        this.processingPromise = null;
      });
    }
  }

  async waitForSettled() {
    await new Promise((resolve) => setTimeout(resolve, ROW_RENDER_DELAY_MS));
    await new Promise((resolve) => requestAnimationFrame(resolve));
    await new Promise((resolve) => requestAnimationFrame(resolve));
  }

  private async processQueueAsync(): Promise<void> {
    if (!this.executedInitialWait) {
      this.executedInitialWait = true;
      await this.waitForSettled();
    }
    let lastToken: Token | undefined;

    while (this.tokenQueue.length > 0) {
      const token = this.tokenQueue.shift();
      const nextToken = this.tokenQueue[0];

      /**
       * ROW RENDER DELAY: To create visual separation and allow the colorful border animation
       * to be visible, we introduce a delay when processing ADD_ENTRY or EDIT_ENTRY tokens that follow a VALUE token.
       * This ensures the row is visible with its animated border before the data fills in.
       *
       * For table views: Use the original logic based on lastToken (ADD_ENTRY/EDIT_ENTRY followed by VALUE)
       * For other views (board, gallery, etc): Use nextToken logic (VALUE followed by non-VALUE)
       */
      const isTableView =
        viewState.view.settings.layout.type === DatabaseLayoutType.TABLE;
      const shouldDelayForTable =
        isTableView &&
        token?.tokenType === TokenType.VALUE &&
        (lastToken?.tokenType === TokenType.ADD_ENTRY ||
          lastToken?.tokenType === TokenType.EDIT_ENTRY);
      const shouldDelayForOtherViews =
        !isTableView &&
        token?.tokenType === TokenType.VALUE &&
        nextToken?.tokenType !== TokenType.VALUE;

      if (shouldDelayForTable || shouldDelayForOtherViews) {
        await new Promise((resolve) => setTimeout(resolve, this.rowRenderDelayMs));
      }

      if (token && this.aifcProcessor) {
        try {
          this.aifcProcessor.onToken(token);
          if (this.editScopeTracker !== null) {
            this.trackEditToken(token);
          }
          // eslint-disable-next-line canvas/no-swallow-errors-in-catch
        } catch (error: unknown) {
          logError('Error processing token', new Error(String(error)));
        }
      }

      if (
        token?.tokenType === TokenType.REMOVE_ENTRY ||
        token?.tokenType === TokenType.REMOVE_FIELD
      ) {
        await new Promise((resolve) => setTimeout(resolve, this.rowRenderDelayMs));
      }

      /**
       * COOPERATIVE YIELDING: Critical for responsive streaming experience
       *
       * Without this yield, the while loop would process ALL queued tokens synchronously,
       * blocking the JavaScript main thread and preventing:
       * - New processChunk() calls from executing (poor server response latency)
       * - UI updates and user interactions (app appears frozen)
       * - Network callbacks and other async operations (system degradation)
       *
       * Benefits of yielding:
       * - Immediate responsiveness to new incoming chunks from server
       * - Maintains interactive UI during database creation (progress, cancellation)
       * - Prevents browser tab from becoming unresponsive
       * - Allows other JavaScript operations to continue normally
       *
       * How it works:
       * 1. setTimeout(resolve, 0) schedules a macrotask for the next event loop cycle
       * 2. await suspends this async function and empties the call stack
       * 3. Event loop processes other pending tasks (new chunks, UI events, etc.)
       * 4. When setTimeout callback fires, it resumes this function to process next token
       *
       * This creates cooperative multitasking: process one token → yield control →
       * handle other work → resume processing. Essential for responsive streaming UX.
       * If we wish to improve performance, we can consider using web workers to process the tokens.
       */
      await new Promise((resolve) => setTimeout(resolve, 0));

      lastToken = token;
    }
  }

  public startStreaming(
    contentId: string,
    groupId?: string,
    options?: DatabaseStreamOptions
  ): void {
    this.analyticsSystem.cancelPendingEvaluation();

    const {
      shouldClearContent = false,
      operationType = 'create',
      rowRenderDelayMs = ROW_RENDER_DELAY_MS,
    } = options || {};

    if (this.currentGroupId && this.currentGroupId !== groupId) {
      this.resetStreamingState();
    }

    if (operationType === 'edit') {
      this.editScopeTracker = {
        affectedRowNumbers: new Set(),
        editedFieldsByRow: new Map(),
        currentEditRowNumber: null,
        affectedViewNames: new Set(),
      };
    }

    this.isStreaming = true;
    this.operationType = operationType;
    this.currentGroupId = groupId;
    this.rowRenderDelayMs = rowRenderDelayMs;

    this.csvParser = new StreamingCsvParser((token) => {
      this.tokenQueue.push(token);
    }, operationType);

    const collabManager = collabStateManager.getCollabManager(contentId);
    if (!collabManager?.database) {
      this.aifcProcessor = null;
      this.notify();
      return;
    }

    const cloudId = collabManager.databaseAri.siteId;
    const aifcProcessor = this.createAIFCProcessor(
      collabManager.database,
      cloudId,
      this.csvParser.errorCollector
    );

    if (shouldClearContent) {
      aifcProcessor.clearDatabase();
      this.setDatabaseVersionToSkipMigrations(contentId);
    }

    this.aifcProcessor = aifcProcessor;
    this.notify();
  }

  private trackEditToken(token: Token): void {
    if (this.editScopeTracker === null) {
      return;
    }

    switch (token.tokenType) {
      case TokenType.EDIT_ENTRY:
        this.editScopeTracker.editedFieldsByRow.set(token.rowNumber, new Set());
        this.editScopeTracker.currentEditRowNumber = token.rowNumber;
        break;
      case TokenType.ADD_ENTRY:
        if (token?.rowNumber) {
          this.editScopeTracker.affectedRowNumbers.add(token.rowNumber);
        }
        this.editScopeTracker.currentEditRowNumber = null;
        break;
      case TokenType.VALUE: {
        const { currentEditRowNumber } = this.editScopeTracker;
        if (currentEditRowNumber !== null && token.value !== '') {
          this.editScopeTracker.editedFieldsByRow
            .get(currentEditRowNumber)
            ?.add(token.fieldName);
        }
        break;
      }
      case TokenType.VIEW:
        this.editScopeTracker.affectedViewNames.add(token.name);
        break;
      case TokenType.SORT:
      case TokenType.EDIT_SORT:
      case TokenType.ADD_FILTER:
      case TokenType.EDIT_FILTER:
      case TokenType.ADD_HIDDEN_FIELD:
        this.editScopeTracker.affectedViewNames.add(token.viewName);
        break;
      default:
        break;
    }
  }

  private createAIFCProcessor(
    database: MutableDatabase,
    cloudId: string,
    errorCollector?: CsvErrorCollector
  ): DatabasesAIFCProcessor {
    const agent = new DatabaseAgent(database);
    const streamingState = new StreamingStateManager();
    const valueProcessor = new ValueProcessor(agent, streamingState, errorCollector);
    const fieldProcessor = new FieldProcessor(
      agent,
      cloudId,
      streamingState,
      valueProcessor,
      errorCollector
    );
    const entryProcessor = new EntryProcessor(
      agent,
      streamingState,
      valueProcessor,
      new Map(this.rowIdToEntryIdMappings),
      errorCollector
    );
    const viewProcessor = new ViewProcessor(
      agent,
      streamingState,
      fieldProcessor,
      errorCollector,
      this.operationType
    );
    const aifcProcessor = new DatabasesAIFCProcessor(
      agent,
      streamingState,
      valueProcessor,
      fieldProcessor,
      entryProcessor,
      viewProcessor,
      errorCollector
    );

    return aifcProcessor;
  }

  private setDatabaseVersionToSkipMigrations(contentId: string): void {
    const collabManager = collabStateManager.getCollabManager(contentId);
    if (!collabManager) {
      return;
    }

    const clientDocVersion = MigrationManager.getClientDocVersion();
    const meta = collabManager.database.doc.getMap('_meta');
    meta.set('version', clientDocVersion);
  }

  public async completeStreaming(): Promise<void> {
    if (this.isStreaming) {
      this.processChunk('\n');
    }

    // Wait for all token processing to complete BEFORE setting isStreaming to false
    // This ensures rows keep their animated borders during the delay periods
    if (this.processingPromise) {
      await this.processingPromise;
    }

    // Preserve errors before resetting state
    if (this.csvParser) {
      this.lastErrors = this.csvParser.errorCollector.getErrors();
    }

    if (this.aifcProcessor) {
      this.aifcProcessor.onStreamingEnd();
    }

    setTimeout(() => {
      this.resetStreamingState();
      this.notify();
    }, ROW_RENDER_DELAY_MS);
  }

  public populateRowIdToEntryIdMappings(mappings: Map<string, string>): void {
    this.rowIdToEntryIdMappings = mappings;
  }

  public getErrors(): CsvError[] {
    if (this.csvParser) {
      return this.csvParser.errorCollector.getErrors();
    }
    return this.lastErrors;
  }

  public clearErrors(): void {
    if (this.csvParser) {
      this.csvParser.errorCollector.clearErrors();
    }
    this.lastErrors = [];
  }

  public scheduleActionedEvaluation(
    contentId: string,
    operationType: 'create' | 'edit'
  ): void {
    let snapshotBuilder = undefined;
    // define edit scoped snapshot builder
    if (operationType === 'edit' && this.editScopeTracker !== null) {
      const preExistingEntryIds = new Set(this.rowIdToEntryIdMappings.values());

      const editedFieldNamesByEntry = new Map<string, Set<string>>();
      for (const [rowNumber, fieldNames] of this.editScopeTracker.editedFieldsByRow) {
        const entryId = this.rowIdToEntryIdMappings.get(rowNumber);
        if (entryId !== undefined) {
          editedFieldNamesByEntry.set(entryId, fieldNames);
        }
      }

      const { affectedViewNames } = this.editScopeTracker;
      this.editScopeTracker = null;

      snapshotBuilder = () =>
        buildScopedEditSnapshot(
          contentId,
          editedFieldNamesByEntry,
          preExistingEntryIds,
          affectedViewNames
        );
    }
    this.analyticsSystem.scheduleEvaluation({
      contentId,
      operationType,
      snapshotBuilder,
    });
  }

  private resetStreamingState(): void {
    this.isStreaming = false;
    this.currentGroupId = undefined;
    this.csvParser = null;
    this.aifcProcessor = null;
    this.tokenQueue = [];
    this.processingPromise = null;
    this.rowRenderDelayMs = ROW_RENDER_DELAY_MS;
    this.executedInitialWait = false;
    this.editScopeTracker = null;
  }
}
